'use client';

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/SafeAppContext';
import { useToast } from '../../context/ToastContext';
import { getImageUrl } from '../../config/api';
import { handleImageError } from '../../utils/imageUtils';
import './ProductCard.css';

const ProductCard = ({ product, showNewBadge = false }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();

  const { user, wishlist, addToWishlist, removeFromWishlist, addToCart, currency } = useApp();
  const { showToast } = useToast();

  // Mock data for visual enhancement if missing from API
  const mockRating = product?.rating || 4.5;
  const mockReviewCount = product?.reviews?.length || Math.floor(Math.random() * 50) + 10;
  const mockColors = product?.colors?.length > 0 ? product.colors : ['#FF0000', '#FFD700', '#0000FF'];
  const isNew = product?.newArrival || Math.random() > 0.7; // Randomly assign "NEW" if missing

  const productId = product?._id || product?.id;

  // Check if product is in wishlist
  React.useEffect(() => {
    if (wishlist && product) {
      setIsWishlisted(wishlist.some(item => (item._id || item.id) === productId));
    }
  }, [wishlist, product, productId]);

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!productId) return;

    setLoading(true);
    try {
      if (isWishlisted) {
        await removeFromWishlist(productId);
      } else {
        await addToWishlist(productId);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!productId) return;

    setLoading(true);
    try {
      await addToCart(productId, 1);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (showNewBadge) {
      // For New Arrivals section, always show in Rs. format
      return `Rs. ${price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    const currencySymbols = {
      INR: '₹',
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      SGD: 'S$'
    };

    // Simple currency conversion (in real app, use proper exchange rates)
    const rates = {
      INR: 1,
      USD: 0.012,
      EUR: 0.011,
      GBP: 0.0095,
      CAD: 0.016,
      AUD: 0.018,
      SGD: 0.016
    };

    const convertedPrice = Math.round(price * rates[currency]);
    return `${currencySymbols[currency]}${convertedPrice.toLocaleString()}`;
  };

  if (!product) return null;

  const rawImage = product.images?.[0];
  const rawSrc = typeof rawImage === 'string' ? rawImage : (rawImage?.url || rawImage?.src || '');
  const optimizedSrc = getImageUrl(rawImage, { w: 400, h: 520, q: 72, f: 'auto' }, product.updatedAt?._seconds || product.updatedAt);

  return (
    <div className="product-card">
      <Link to={`/product/${productId}`} className="product-link">
        <div className={`product-image-container ${!imageLoaded ? 'image-loading' : ''}`}>
          <img
            src={optimizedSrc}
            data-original-src={rawSrc}
            alt={product.name || 'Product'}
            className={`product-image ${imageLoaded ? 'loaded' : 'loading'}`}
            loading="lazy"
            decoding="async"
            width="400"
            height="520"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => handleImageError(e, setImageLoaded)}
          />

          {showNewBadge && (
            <span className="product-badge new-arrivals-badge">New Arrivals</span>
          )}

          {!showNewBadge && isNew && (
            <span className="product-badge new-badge">NEW</span>
          )}

          <button
            className={`product-wishlist-icon ${isWishlisted ? 'active' : ''}`}
            onClick={handleWishlistToggle}
            disabled={loading}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <i className={`fa-${isWishlisted ? 'solid' : 'regular'} fa-heart`}></i>
          </button>

          <div className="product-overlay">
            <button
              className="overlay-btn add-to-cart-btn product-add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={loading}
              aria-label="Add to Bag"
            >
              {loading ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-bag-shopping"></i>
              )}
              <span>{loading ? 'ADDING...' : 'ADD TO BAG'}</span>
            </button>
          </div>
        </div>

        <div className="product-info">
          <h3 className="product-name">{product.name}</h3>

          <div className="product-price">
            {product.originalPrice && Number(product.originalPrice) > Number(product.price) && (
              <span className="original-price">{formatPrice(product.originalPrice)}</span>
            )}
            <span className="current-price">{formatPrice(product.price)}</span>
          </div>

          {!showNewBadge && (
            <>
          <div className="product-rating">
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`fa-${i < Math.floor(mockRating) ? 'solid' : 'regular'} fa-star`}
                ></i>
              ))}
            </div>
            <span className="rating-text">({mockReviewCount})</span>
          </div>
            </>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;